import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import b2bService from '../../services/b2bService';
import './B2bPortalLoginPage.css';

const B2bPortalLoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(() => {
    try {
      const notice = sessionStorage.getItem('b2b_portal_notice');
      sessionStorage.removeItem('b2b_portal_notice');
      return notice;
    } catch {
      return null;
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please provide your client username and password.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await b2bService.portalLogin(username, password);
      if (data && data.token) {
        // Store in sessionStorage to guarantee Zero-Trust Tenant isolation from internal staff credentials
        sessionStorage.setItem('b2b_portal_token', data.token);
        sessionStorage.setItem(
          'b2b_portal_user',
          JSON.stringify({
            userId: data.userId,
            partyId: data.partyId,
            partyCode: data.partyCode,
            partyName: data.partyName,
            fullName: data.fullName,
            role: data.role,
            creditLimit: data.creditLimit,
            allowPortalRegistration: data.allowPortalRegistration !== false
          })
        );

        navigate('/b2b/portal/dashboard');
      } else {
        setError('Authentication response did not contain a valid session token.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Login failed. Please check credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="b2b-login-container">
      <div className="b2b-login-orb orb-a" />
      <div className="b2b-login-orb orb-b" />
      <div className="b2b-login-orb orb-c" />
      <div className="b2b-login-grid" />

      <div className="b2b-login-brand-corner">
        <span className="b2b-login-brand-mark">i</span>
        <span className="b2b-login-brand-name">inTeLiSYS</span>
      </div>

      <div className="b2b-login-split">
        <section className="b2b-login-hero">
          <span className="b2b-login-badge">Client Partner Portal</span>
          <h1 className="b2b-login-hero-title">
            Your lab results,<br />
            <span>approved and on time.</span>
          </h1>
          <p className="b2b-login-hero-sub">
            Track patient orders, open approved reports, review invoices and your account statement, all in one secure place.
          </p>
          <ul className="b2b-login-points">
            <li><span>🧪</span> Live order and report status</li>
            <li><span>🧾</span> Invoices and ledger statement</li>
            <li><span>🔒</span> Reports open only after approval</li>
          </ul>
        </section>

        <div className="b2b-login-card">
          <div className="b2b-login-header">
            <h2 className="b2b-login-title">Welcome back</h2>
            <p className="b2b-login-subtitle">Sign in with your client account</p>
          </div>

          {error && <div className="b2b-login-alert" role="alert">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="b2b-form-group">
              <label className="b2b-form-label" htmlFor="b2b-username">Client Username</label>
              <input
                id="b2b-username"
                type="text"
                className="b2b-form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your client username"
                autoComplete="username"
                required
                autoFocus
              />
            </div>

            <div className="b2b-form-group">
              <label className="b2b-form-label" htmlFor="b2b-password">Password</label>
              <div className="b2b-pass-wrap">
                <input
                  id="b2b-password"
                  type={showPassword ? 'text' : 'password'}
                  className="b2b-form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="b2b-pass-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button type="submit" className="b2b-submit-btn" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In to Portal'}
            </button>
          </form>

          <div className="b2b-login-footer">
            <div>Laboratory staff member?</div>
            <Link to="/login" className="b2b-back-link">
              &larr; Go to the laboratory login
            </Link>
          </div>
        </div>
      </div>

      <div className="b2b-login-foot">© inTeLiSYS &bull; Laboratory Information System</div>
    </div>
  );
};

export default B2bPortalLoginPage;
